import PropTypes from 'prop-types';
import React from 'react';
import {debounce} from 'ts-debounce';

import * as MediocreConstants from 'notebook/mediocre/MediocreConstants';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  DROPDOWN_SEARCH_RESULTS_RENDERED_EVENT,
  DROPDOWN_SEARCH_STARTED_EVENT,
} from 'performance/processSearchEvents';
import {MIN_QUERY_LENGTH, REFINE_INPUT_MESSAGE, SHORT_QUERY_MESSAGE} from 'search/SearchConstants';
import {trackSelectedItem} from 'search/searchTracking';
import {ElementKeyboardEvent} from 'util/Types';

import AttachDropdown from './AttachDropdown';
import {AttachDropdownItem, OptionHeight} from './AttachDropdownConstants';
import {CustomAttachDropdownOption} from './AttachDropdownOption';
import {RenderAttachDropdownOptionInfo, SelectAttachment} from './AttachDropdownOptionsMenu';
import getDefaultRenderAttachDropdownOptionInfo from './getDefaultRenderAttachDropdownOptionInfo';

const SHORT_QUERY_SENTINEL: AttachDropdownItem = {name: '', item_id: '', item: {}};
const REFINE_INPUT_SENTINEL: AttachDropdownItem = {name: REFINE_INPUT_MESSAGE, item_id: '', item: {}};

const emptyState: State = {
  results: [],
  searching: false,
  errorMessage: null,
  searchCorrelationId: null,
};

export interface SearchResultInfo {
  query: string;
  limit: number;
  results: Array<AttachDropdownItem>;
  errorMessage: string | null;
  correlationId?: string | null;
}

export interface ProfilerTrackingParams {
  url: string;
  searchType: string;
}

interface SearchDropdownProps {
  allowEmptyQuery?: boolean;
  defaultOptionSelected?: boolean;
  // A function with parameters (query, searchResultsCallback). On completion or failure,
  // searchResultsCallback should be called with an object of the form {query, results, errorMessage}.
  doSearch: (
    query: string,
    searchResultsCallback: ({query, limit, results, errorMessage, correlationId}: SearchResultInfo) => void
  ) => void;
  query: string;
  getRenderOptionInfo?: (result: AttachDropdownItem) => RenderAttachDropdownOptionInfo;
  selectAttachment: SelectAttachment;
  width?: string | number;
  profilerTrackingParams: ProfilerTrackingParams;
}

interface State {
  results: Array<AttachDropdownItem>;
  searching: boolean;
  errorMessage: string | null;
  searchCorrelationId?: string | null;
}

export default class SearchDropdown extends React.Component<SearchDropdownProps, State> {
  static propTypes = {
    allowEmptyQuery: PropTypes.bool,
    defaultOptionSelected: PropTypes.bool,
    // A function with parameters (query, searchResultsCallback). On completion or failure,
    // searchResultsCallback should be called with an object of the form {query, results, errorMessage}.
    doSearch: PropTypes.func.isRequired,
    query: PropTypes.string.isRequired,
    // Remaining props forwarded to AttachDropdown.
  };
  static DEBOUNCE_MS = 500;

  private _isMounted: boolean = false;
  private doSearchDebounced: (() => void) | null = null;

  attachDropdownRef = React.createRef<AttachDropdown>();
  state: State = emptyState;

  componentDidMount(): void {
    this.doSearchDebounced = debounce(() => {
      this.props.doSearch(this.props.query, this._handleSearchResults);
    }, SearchDropdown.DEBOUNCE_MS);

    if (this.props.allowEmptyQuery || this.props.query !== '') {
      this.setState({searching: true});
      this.doSearchDebounced();
    }

    this._isMounted = true;
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  UNSAFE_componentWillReceiveProps(nextProps: SearchDropdownProps): void {
    if (nextProps.query === '' && !this.props.allowEmptyQuery) {
      this.setState(emptyState);
    } else if (nextProps.query !== this.props.query) {
      this.setState({searching: true});
      logProfilerTimingEvent(DROPDOWN_SEARCH_STARTED_EVENT);
      this.doSearchDebounced!();
    }
  }

  _handleSearchResults = ({query, limit, results, errorMessage, correlationId}: SearchResultInfo): void => {
    if (this._isMounted && query === this.props.query) {
      if (query.length > 0 && query.length < MIN_QUERY_LENGTH) {
        results = [SHORT_QUERY_SENTINEL, ...results];
      } else if (results.length === limit) {
        results = [REFINE_INPUT_SENTINEL, ...results];
      }
      this.setState({results, errorMessage, searching: false, searchCorrelationId: correlationId});
      logProfilerTimingEvent(DROPDOWN_SEARCH_RESULTS_RENDERED_EVENT, {
        url: this.props.profilerTrackingParams.url,
        searchType: this.props.profilerTrackingParams.searchType,
      });
    }
  };

  _handleSelectAttachment = (
    id: string | number,
    name: string,
    url: string | null,
    item: object,
    internalLink: MediocreConstants.InternalLink | undefined,
    index: number
  ): void => {
    trackSelectedItem(id.toString(), index + 1, this.state.searchCorrelationId);
    this.props.selectAttachment(id, name, url, item, internalLink, index);
  };

  _getRenderResultInfo = (result: AttachDropdownItem): RenderAttachDropdownOptionInfo => {
    if (result === SHORT_QUERY_SENTINEL) {
      return {
        customHeight: OptionHeight.MEDIUM,
        optionContent: <CustomAttachDropdownOption title={SHORT_QUERY_MESSAGE} />,
        hoverable: false,
        clickable: false,
      };
    }
    if (result === REFINE_INPUT_SENTINEL) {
      return {
        customHeight: OptionHeight.MEDIUM,
        optionContent: <CustomAttachDropdownOption title={result.name} />,
        hoverable: false,
        clickable: false,
      };
    }

    return this.props.getRenderOptionInfo
      ? this.props.getRenderOptionInfo(result)
      : getDefaultRenderAttachDropdownOptionInfo(result);
  };

  queryKeydownHandler(e: ElementKeyboardEvent): boolean {
    return this.attachDropdownRef.current!.queryKeydownHandler(e);
  }

  render(): JSX.Element {
    return (
      <AttachDropdown
        ref={this.attachDropdownRef}
        defaultOptionSelected={this.props.defaultOptionSelected}
        errorMessage={this.state.errorMessage}
        results={this.state.results}
        searching={this.state.searching}
        {...this.props}
        selectAttachment={this._handleSelectAttachment}
        // Prefer our own callback over any parents might have been specified.
        getRenderOptionInfo={this._getRenderResultInfo}
      />
    );
  }
}
